<template>
  <div>
    <label v-if="label" class="block text-sm font-medium text-gray-700 mb-1">{{ label }}{{ required ? ' *' : '' }}</label>
    <div class="relative">
      <input
        ref="inputRef"
        type="text"
        :value="displayValue"
        :placeholder="placeholder"
        :required="required"
        @mousedown="onMouseDown"
        @focus="onFocus"
        @input="onInput"
        @keydown.escape="closeDropdown"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.arrow-down.prevent="highlightNext"
        @keydown.arrow-up.prevent="highlightPrev"
        @blur="scheduleBlurClose"
        :class="[
          'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500',
          loading || showClearButton ? 'pr-10' : ''
        ]"
      />
      <button
        v-if="showClearButton"
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 z-20 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label="Limpar cidade"
        @mousedown.stop.prevent="clearSelection"
      >
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div v-if="loading" class="absolute right-3 top-2.5 z-10 pointer-events-none">
        <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
      </div>
      <div
        v-if="isOpen"
        class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 max-h-64 overflow-y-auto"
        @mousedown.stop="cancelScheduledBlur"
      >
        <div
          v-for="(opt, idx) in dropdownRows"
          :key="opt.id"
          role="option"
          tabindex="-1"
          @mousedown.stop="pick(opt)"
          :class="[
            'px-4 py-2 cursor-pointer text-gray-900 select-none',
            idx === highlightedIndex ? 'bg-indigo-100' : 'hover:bg-indigo-50'
          ]"
        >
          {{ opt.label }}
        </div>
        <div v-if="!loading && dropdownRows.length === 0 && filterTextTrimmed.length >= minLength" class="px-4 py-2 text-gray-500">
          Nenhuma cidade encontrada
        </div>
        <div v-if="filterTextTrimmed.length > 0 && filterTextTrimmed.length < minLength" class="px-4 py-2 text-sm text-gray-500">
          Digite pelo menos {{ minLength }} caracteres
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CitySelectAsync',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    /** UF (2 letras) para restringir a busca, ex. vindo do campo Estado */
    uf: {
      type: String,
      default: ''
    },
    /** Rótulo quando o registro veio da API (edição) e ainda não está em `options` */
    selectedLabel: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Cidade'
    },
    placeholder: {
      type: String,
      default: 'Digite para buscar cidade...'
    },
    required: {
      type: Boolean,
      default: false
    },
    minLength: {
      type: Number,
      default: 2
    },
    /** Exibe botão para limpar seleção (ex.: filtro de listagem) */
    clearable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'city-picked'],
  data() {
    return {
      isOpen: false,
      filterText: '',
      options: [],
      loading: false,
      highlightedIndex: -1,
      searchTimeout: null,
      blurTimeout: null
    }
  },
  computed: {
    filterTextTrimmed() {
      return (this.filterText || '').trim()
    },
    displayValue() {
      if (this.isOpen) return this.filterText
      if (this.modelValue === '' || this.modelValue == null) return ''
      const hit = this.options.find((o) => String(o.id) === String(this.modelValue))
      if (hit) return hit.label
      if (this.selectedLabel) return this.selectedLabel
      return ''
    },
    dropdownRows() {
      return this.options
    },
    showClearButton() {
      if (!this.clearable || this.loading) return false
      const v = this.modelValue
      return v !== '' && v != null
    }
  },
  watch: {
    uf(newUf, oldUf) {
      if (newUf !== oldUf && this.modelValue) {
        this.$emit('update:modelValue', '')
        this.$emit('city-picked', { uf: '', label: '' })
        this.options = []
      }
    }
  },
  beforeUnmount() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    if (this.blurTimeout) clearTimeout(this.blurTimeout)
  },
  methods: {
    stripAccents(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    },
    async fetchSearch(q) {
      const term = (q || '').trim()
      if (term.length < this.minLength) {
        this.options = []
        return
      }
      this.loading = true
      try {
        // O banco salva `cities.name` sem acento; então removemos acento antes de consultar.
        const termNoAccent = this.stripAccents(term)
        const params = new URLSearchParams({ q: termNoAccent })
        const uf = (this.uf || '').trim().toUpperCase()
        if (uf.length === 2) params.set('uf', uf)
        const res = await fetch(`/api/v1/cities/search?${params.toString()}`)
        if (!res.ok) {
          this.options = []
          return
        }
        const data = await res.json()
        const list = data.cities || []
        this.options = list.map((c) => ({
          id: c.id,
          label: c.label,
          uf: c.uf
        }))
      } catch (e) {
        console.error('CitySelectAsync:', e)
        this.options = []
      } finally {
        this.loading = false
      }
    },
    onMouseDown() {
      if (!this.isOpen) {
        this.isOpen = true
        this.highlightedIndex = -1
        if (this.filterTextTrimmed.length >= this.minLength) this.fetchSearch(this.filterTextTrimmed)
      }
    },
    onFocus() {
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout)
        this.blurTimeout = null
      }
      this.isOpen = true
      this.highlightedIndex = -1
    },
    onInput(e) {
      this.filterText = e.target.value
      this.isOpen = true
      this.highlightedIndex = -1
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.fetchSearch(this.filterTextTrimmed)
      }, 300)
    },
    pick(opt) {
      if (!opt || opt.id == null) return
      this.cancelScheduledBlur()
      this.$emit('update:modelValue', opt.id)
      this.$emit('city-picked', { uf: opt.uf, label: opt.label })
      this.isOpen = false
      this.filterText = ''
      this.highlightedIndex = -1
    },
    selectHighlighted() {
      const row = this.dropdownRows[this.highlightedIndex]
      if (row) this.pick(row)
    },
    highlightNext() {
      if (!this.dropdownRows.length) return
      this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.dropdownRows.length - 1)
    },
    highlightPrev() {
      if (!this.dropdownRows.length) return
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
    },
    closeDropdown() {
      this.isOpen = false
      this.filterText = ''
      this.highlightedIndex = -1
    },
    scheduleBlurClose() {
      this.blurTimeout = setTimeout(() => {
        this.closeDropdown()
        this.blurTimeout = null
      }, 250)
    },

    cancelScheduledBlur() {
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout)
        this.blurTimeout = null
      }
    },

    clearSelection() {
      this.cancelScheduledBlur()
      this.filterText = ''
      this.highlightedIndex = -1
      this.isOpen = false
      this.options = []
      this.$emit('update:modelValue', '')
      this.$emit('city-picked', { uf: '', label: '' })
      this.$nextTick(() => this.$refs.inputRef?.focus?.())
    },
    /** Chamado após ViaCEP: tenta casar município + UF com uma única linha da API */
    async tryResolveFromViaCep(localidade, uf) {
      const name = (localidade || '').trim()
      const u = (uf || '').trim().toUpperCase()
      if (name.length < 2 || u.length !== 2) return
      this.loading = true
      try {
        // ViaCEP pode vir com acento; como o `cities.name` é persistido sem acento,
        // consultamos já com acento removido.
        const nameNoAccent = this.stripAccents(name)
        const params = new URLSearchParams({ q: nameNoAccent.slice(0, 40) })
        params.set('uf', u)
        const res = await fetch(`/api/v1/cities/search?${params.toString()}`)
        if (!res.ok) return
        const data = await res.json()
        const list = data.cities || []
        const norm = (s) =>
          String(s)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
        const nq = norm(name)
        const exact = list.filter((c) => norm(c.display_name || c.name) === nq || norm(c.name) === nq)
        // Preferir match exato; se tiver mais de um (raramente), pega o primeiro.
        const pickRow = exact.length >= 1 ? exact[0] : list.length === 1 ? list[0] : null
        if (pickRow) {
          const c = pickRow
          this.$emit('update:modelValue', c.id)
          this.$emit('city-picked', { uf: c.uf, label: c.label })
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
